import { useCallback, useEffect, useState } from 'react';

import { deleteUser, listUsers } from '../api.js';
import Icon from '../ui/Icon.jsx';
import Modal from '../ui/Modal.jsx';
import { notify } from '../ui/notify.jsx';
import UserModal from './UserModal.jsx';
import { isLastAdmin } from './users.js';

export default function UsersPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await listUsers());
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) return <p className="error">{error}</p>;
  if (!data) return <p className="empty">Loading…</p>;

  const { roles, domain, users } = data;

  return (
    <>
      <div className="register-header">
        <h2>Users</h2>
        <button type="button" className="primary" onClick={() => setModal({ mode: 'create' })}>
          <Icon name="plus" /> Add user
        </button>
      </div>

      {users.length === 0 ? (
        <p className="empty">No users.</p>
      ) : (
        <div className="table-scroll">
          <table className="data-table users-table">
            <thead>
              <tr>
                <th className="plain">First name</th>
                <th className="plain">Last name</th>
                <th className="plain">Email</th>
                {roles.map((role) => (
                  <th key={role} className="plain">{role}</th>
                ))}
                <th className="plain">Edit</th>
                <th className="plain">Delete</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.email}>
                  <td>{user.first_name}</td>
                  <td>{user.last_name}</td>
                  <td>{user.email}</td>
                  {roles.map((role) => (
                    <td key={role} className="role-cell">
                      {user.roles.includes(role) && (
                        <Icon name="check" />
                      )}
                    </td>
                  ))}
                  <td className="row-actions">
                    <button
                      type="button"
                      className="icon-button quiet"
                      aria-label={`Edit ${user.first_name} ${user.last_name}`}
                      onClick={() => setModal({ mode: 'edit', user })}
                    >
                      <Icon name="pencil" />
                    </button>
                  </td>
                  <td className="row-actions">
                    <button
                      type="button"
                      className="icon-button quiet"
                      aria-label={`Delete ${user.first_name} ${user.last_name}`}
                      onClick={() => setDeleteTarget(user)}
                    >
                      <Icon name="trash" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <UserModal
          mode={modal.mode}
          user={modal.user}
          roles={roles}
          users={users}
          domain={domain}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      )}

      {deleteTarget && (
        <DeleteUserDialog
          user={deleteTarget}
          users={users}
          onClose={() => setDeleteTarget(null)}
          onDeleted={load}
        />
      )}
    </>
  );
}

function DeleteUserDialog({ user, users, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const lastAdmin = isLastAdmin(users, user.email) && user.roles.includes('ADMIN');

  const onSubmit = async (event) => {
    event.preventDefault();

    if (lastAdmin) {
      setError('Cannot delete the only remaining admin.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await deleteUser(user.email);
      notify(`${user.first_name} ${user.last_name} deleted.`);
      onDeleted();
      onClose();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <form onSubmit={onSubmit}>
        <h3>Delete {user.first_name} {user.last_name}?</h3>
        <p className="hint">
          This removes the user from T-REX. They will no longer be able to sign in.
        </p>
        {lastAdmin && (
          <p className="error">
            This is the only admin. Delete another admin first, or assign Admin to someone else.
          </p>
        )}
        {error && !lastAdmin && <p className="error">{error}</p>}
        <menu>
          <button type="button" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" disabled={busy || lastAdmin}>
            {busy ? 'Deleting…' : 'Delete'}
          </button>
        </menu>
      </form>
    </Modal>
  );
}
